// this part display post on the userdashboard for scheduled notes for today
import { useState, useEffect } from 'react'
import { FiBell, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import axios from 'axios'
import { DateTime } from 'luxon'

export default function ScheduledNotesWidget({ userTimezone = 'UTC' }) {
  const [scheduledNotes, setScheduledNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    fetchScheduledNotes()
    // Refresh every minute to catch updates and notes that have passed their scheduled time
    const interval = setInterval(fetchScheduledNotes, 60000)
    
    // Listen for note creation/update events to refresh immediately
    const handleNoteCreated = () => {
      console.log('Note created event received, refreshing scheduled notes')
      fetchScheduledNotes()
    }
    
    window.addEventListener('noteCreated', handleNoteCreated)
    window.addEventListener('noteUpdated', handleNoteCreated)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('noteCreated', handleNoteCreated)
      window.removeEventListener('noteUpdated', handleNoteCreated)
    }
  }, [userTimezone])

  const fetchScheduledNotes = async () => {
    try {
      setLoading(true)
      console.log('🔄 ScheduledNotesWidget: Fetching notes for timezone:', userTimezone)
      
      // Try to use the optimized endpoint first
      let response
      try {
        console.log('📍 Requesting /notes/scheduled/today with timezone:', userTimezone)
        response = await axios.get('/notes/scheduled/today', {
          params: { timezone: userTimezone }
        })
        
        const todayNotes = response.data.notes || []
        console.log('✅ ScheduledNotesWidget: Backend returned', todayNotes.length, 'notes for today')
        console.log('📋 Notes data:', JSON.stringify(todayNotes, null, 2))
        
        // Validate note structure
        todayNotes.forEach((note, idx) => {
          console.log(`📌 Note ${idx}:`, {
            id: note._id,
            title: note.title,
            scheduleDate: note.scheduleDate,
            scheduledTime: note.scheduledTime,
            timezone: note.timezone,
            scheduledUTC: note.scheduledUTC
          })
        })
        
        setScheduledNotes(todayNotes)
        setCurrentIndex(0)
      } catch (error) {
        // Fallback to filtering client-side if optimized endpoint doesn't exist
        console.warn('⚠️ ScheduledNotesWidget: Backend endpoint error - Status:', error.response?.status, 'Message:', error.message)
        console.warn('Full error:', error)
        
        if (error.response?.status === 404 || error.response?.status === 403) {
          console.log('📝 ScheduledNotesWidget: Falling back to client-side filtering...')
          
          const allNotesResponse = await axios.get('/notes', {
            params: { archived: false, limit: 100 }
          })
          
          const notes = allNotesResponse.data.notes || []
          console.log('📦 Fetched', notes.length, 'total notes from backend')
          
          // Get today's date in user's timezone
          const userToday = DateTime.now().setZone(userTimezone).startOf('day')
          console.log('📅 Today in user timezone:', userToday.toISO())

          // Filter notes that are scheduled for today in user's timezone
          const todayNotes = notes.filter(note => {
            // Note must have a scheduledUTC field
            if (!note.scheduledUTC) {
              console.warn('⚠️ Note missing scheduledUTC:', note._id)
              return false
            }

            try {
              // Use note's timezone if available, otherwise use userTimezone
              const noteTimezone = note.timezone || userTimezone
              
              // Parse the scheduledUTC (stored in UTC) and convert to user's timezone
              const scheduledDateTime = DateTime.fromISO(note.scheduledUTC, { zone: 'UTC' })
                .setZone(noteTimezone)
              
              // Get the start of the scheduled day in user's timezone
              const scheduledDay = scheduledDateTime.startOf('day')
              
              // Compare dates
              const isToday = scheduledDay.equals(userToday)
              if (isToday) {
                console.log('✓ Note scheduled for today:', note.title, '|', note.scheduledUTC)
              }
              return isToday
            } catch (error) {
              console.error('Error parsing scheduledUTC for note:', note._id, note.scheduledUTC, error)
              return false
            }
          }).sort((a, b) => {
            // Sort by scheduled time ascending
            try {
              const timeA = DateTime.fromISO(a.scheduledUTC, { zone: 'UTC' }).toMillis()
              const timeB = DateTime.fromISO(b.scheduledUTC, { zone: 'UTC' }).toMillis()
              return timeA - timeB
            } catch (error) {
              return 0
            }
          })
          
          console.log('📌 After filtering:', todayNotes.length, 'notes for today')
          setScheduledNotes(todayNotes)
          setCurrentIndex(0)
        } else {
          throw error
        }
      }
    } catch (error) {
      console.error('❌ Error fetching scheduled notes:', error)
      setScheduledNotes([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center gap-3 text-white">
          <div className="p-2 bg-white bg-opacity-20 rounded-lg">
            <FiBell className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Loading scheduled notes...</h3>
            <p className="text-xs text-blue-100">This may take a moment</p>
          </div>
        </div>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded-lg w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded-lg w-full"></div>
            <div className="h-4 bg-gray-200 rounded-lg w-5/6"></div>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="h-12 bg-gray-100 rounded-lg"></div>
              <div className="h-12 bg-gray-100 rounded-lg"></div>
              <div className="h-12 bg-gray-100 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (scheduledNotes.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center gap-3 text-white">
          <div className="p-2 bg-white bg-opacity-20 rounded-lg">
            <FiBell className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">No Scheduled Notes</h3>
            <p className="text-xs text-blue-100">You have no notes scheduled for today</p>
          </div>
        </div>
        <div className="p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <FiBell className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No Notes Today</h4>
          <p className="text-sm text-gray-600">
            Create a note and schedule it to see it appear here
          </p>
        </div>
      </div>
    )
  }

  const currentNote = scheduledNotes[currentIndex]
  const hasMultiple = scheduledNotes.length > 1

  // Format scheduled date and time for display
  const formatScheduledDateTime = (note) => {
    try {
      const noteTimezone = note.timezone || userTimezone
      
      // Use scheduleDate string directly if available (exact user selection)
      let dateStr = 'Invalid date'
      let timeStr = '00:00'
      
      if (note.scheduleDate && note.scheduledTime) {
        // Both fields available - use them directly (most accurate)
        const dt = DateTime.fromISO(note.scheduleDate)
        if (dt.isValid) {
          dateStr = dt.toFormat('MMM dd, yyyy')
          timeStr = note.scheduledTime
        }
      } else if (note.scheduledUTC) {
        // Fallback to reconverting from UTC
        const dt = DateTime.fromISO(note.scheduledUTC, { zone: 'UTC' })
          .setZone(noteTimezone)
        if (dt.isValid) {
          dateStr = dt.toFormat('MMM dd, yyyy')
          timeStr = dt.toFormat('HH:mm')
        }
      }
      
      return { dateStr, timeStr, timezone: noteTimezone }
    } catch (error) {
      console.error('Error formatting scheduled date:', error, note)
      return {
        dateStr: 'Date unavailable',
        timeStr: note?.scheduledTime || '00:00',
        timezone: userTimezone
      }
    }
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? scheduledNotes.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === scheduledNotes.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 text-white">
          <div className="p-2 bg-white bg-opacity-20 rounded-lg">
            <FiBell className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Today's Scheduled Notes</h3>
            <p className="text-xs text-blue-100">
              {scheduledNotes.length} {scheduledNotes.length === 1 ? 'note' : 'notes'} scheduled
            </p>
          </div>
        </div>
        {hasMultiple && (
          <span className="text-xs font-semibold bg-white bg-opacity-20 text-white px-3 py-1 rounded-full">
            {currentIndex + 1} of {scheduledNotes.length}
          </span>
        )}
      </div>

      {/* Note Display */}
      <div className="p-6">
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-100 p-5 mb-4">
          {/* Title */}
          <h4 className="font-bold text-gray-900 text-lg mb-3 line-clamp-2">
            {currentNote.title}
          </h4>
          
          {/* Content Preview */}
          {currentNote.content && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
              {currentNote.content}
            </p>
          )}
          
          {/* Date, Time, and Timezone */}
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</div>
              <div className="text-sm font-semibold text-blue-700">
                {formatScheduledDateTime(currentNote).dateStr}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time</div>
              <div className="text-sm font-semibold text-blue-700">
                {formatScheduledDateTime(currentNote).timeStr}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">TZ</div>
              <div className="text-xs font-semibold text-gray-600 truncate">
                {formatScheduledDateTime(currentNote).timezone}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        {hasMultiple && (
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={goToPrevious}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-blue-600"
              title="Previous note"
              aria-label="Previous note"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            
            {/* Dot indicators */}
            <div className="flex gap-2 items-center px-4 py-2 bg-gray-50 rounded-lg">
              {scheduledNotes.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'w-3 h-3 bg-blue-600' 
                      : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={`Note ${idx + 1} of ${scheduledNotes.length}`}
                  aria-label={`Go to note ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-blue-600"
              title="Next note"
              aria-label="Next note"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}














